import {create} from "@bufbuild/protobuf";
import {LocationSchema, TournamentState} from "../../generated/smg_service_pb";
import image8 from "../assets/img/image8.png";
import image9 from "../assets/img/acfdd669d263e06734c69ca200dd47a7.png";
import {Platform} from "react-native";
import moment from 'moment';
import {colors} from "../assets/styles/colors";
import * as faceapi from "face-api.js";
import {timestampFromDate, type Timestamp} from "@bufbuild/protobuf/wkt";
import {FormikProps} from "formik";
import {ExtendedUserDataType} from "../types";

export const getTimeLeft = (endTimestamp: number) => {
  const now = Math.floor(Date.now() / 1000);
  let secondsLeft = endTimestamp - now;

  if (secondsLeft < 0) secondsLeft = 0;

  const ONE_MIN = 60;
  const ONE_HOUR = 60 * ONE_MIN;
  const ONE_DAY = 24 * ONE_HOUR;
  const ONE_WEEK = 7 * ONE_DAY;

  if (secondsLeft <= ONE_DAY) {
    const hours = String(Math.floor(secondsLeft / ONE_HOUR)).padStart(2, '0');
    const minutes = String(Math.floor((secondsLeft % ONE_HOUR) / ONE_MIN)).padStart(2, '0');
    const seconds = String(secondsLeft % ONE_MIN).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  const weeks = Math.floor(secondsLeft / ONE_WEEK);
  secondsLeft %= ONE_WEEK;

  const days = Math.floor(secondsLeft / ONE_DAY);
  secondsLeft %= ONE_DAY;

  const hours = String(Math.floor(secondsLeft / ONE_HOUR)).padStart(2, '0');
  const minutes = String(Math.floor((secondsLeft % ONE_HOUR) / ONE_MIN)).padStart(2, '0');
  const seconds = String(secondsLeft % ONE_MIN).padStart(2, '0');

  const parts: string[] = [];

  if (weeks >= 1) parts.push(`${weeks} ${weeks === 1 ? 'Week' : 'Weeks'}`);
  if (days >= 1) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);

  parts.push(`${hours}:${minutes}:${seconds}`);

  return parts.join(', ');
};

export const footFormatter = new Intl.NumberFormat('en-US', {
  style: 'unit', unit: 'foot', unitDisplay: 'long', maximumFractionDigits: 0
});

// Assumes a date-only ISO string (YYYY-MM-DD).
// For inputs with time, timestampFromDate will preserve milliseconds in nanos.
export const convertToUnixTimestamp = (isoDateString: string): Timestamp => {
  return timestampFromDate(moment(isoDateString).utc(true).toDate());
};

export const formatPayout = (amount: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2
  });
  return formatter.format(amount);
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: value % 1 === 0 ? 0 : 2, maximumFractionDigits: 2
  }).format(value);
};

export const getFieldError = (key: string, formik: FormikProps<any>) => {
  return formik.touched[key] && formik.errors[key];
};

const getAveragePoint = (points: faceapi.Point[]) => {
  const sum = points.reduce((acc, point) => {
    return {x: acc.x + point.x, y: acc.y + point.y};
  }, {x: 0, y: 0});

  return {x: sum.x / points.length, y: sum.y / points.length};
};

export const detectHeadTurn = async (video: HTMLVideoElement | null) => {
  if (!video) return;
  const detections = await faceapi.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks();

  if (detections) {
    const landmarks = detections.landmarks;

    const leftEye = getAveragePoint(landmarks.getLeftEye());
    const rightEye = getAveragePoint(landmarks.getRightEye());
    const nose = getAveragePoint(landmarks.getNose());

    const eyeDistance = rightEye.x - leftEye.x;
    const centerPosition = leftEye.x + eyeDistance / 2;
    const noseOffset = nose.x - centerPosition;

    const threshold = eyeDistance * 0.25;

    if (noseOffset < -threshold) return 'left';
    else if (noseOffset > threshold) return 'right';
    else return 'straight';
  }
};

export const tournamentStatusStyle = (state?: TournamentState) => {
  let val = {color: '', text: ''};

  if (state === TournamentState.PENDING) val = {color: colors.filter_chip_bg, text: 'CREATED'};
  else if (state === TournamentState.PUBLISHED) val = {color: colors.disable_chip, text: 'PUBLISHED'};
  else if (state === TournamentState.PLAYING) val = {color: colors.state_color, text: 'ACTIVE'};
  else if (state === TournamentState.FINISHED) val = {color: colors.state_completed, text: 'Completed'};
  else if (state === TournamentState.CANCELLED) val = {color: colors.state_cancelled, text: 'CANCELLED'};
  else if (state === TournamentState.VOIDED) val = {color: colors.state_cancelled, text: 'VOIDED'};

  return val;
};

// tournamentShotsBeforeEntry — how many onboarding free shots a new player must
// still complete before tournament entry unlocks. Drives the "Tournament Starts
// After N Shots" card banner (SMG-3602).
//
// Computed from the source-INDEPENDENT `completedFreeShots` (the raw count of
// free shots already finished) rather than the overloaded `freeShotNumber`.
// This matters because freeShotNumber has two different meanings depending on
// which writer last set it: GetUser reports it as a 1-indexed CURRENT-shot
// pointer, while the bay-session PlayerState stream reports it as the raw
// completed count. Both writers populate `completedFreeShots` identically, so
// remaining shots is simply freeShotCount - completedFreeShots — correct no
// matter which backend last refreshed extendedUserData.
//
// Guarding on isNewPlayer stops the banner sticking after onboarding completes
// (completedFreeShots == freeShotCount would otherwise still yield 0, but the
// guard is the authoritative "still onboarding" flag) and avoids a phantom
// count for players who never had free shots. The Math.max clamp defends
// against a negative/over-range value (over-completed count) yielding a
// nonsensical banner. The caller renders only when > 0.
export const tournamentShotsBeforeEntry = (extendedUserData: ExtendedUserDataType): number => {
  if (!extendedUserData.isNewPlayer) return 0;
  return Math.max(0, extendedUserData.freeShotCount - extendedUserData.completedFreeShots);
};

const distance = (lat1: number, lon1: number, lat2: number, lon2: number, unit: 'M' | 'K' | 'N') => {
  if ((lat1 == lat2) && (lon1 == lon2)) return 0;
  else {
    const radlat1 = Math.PI * lat1 / 180;
    const radlat2 = Math.PI * lat2 / 180;
    const theta = lon1 - lon2;
    const radtheta = Math.PI * theta / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

    if (dist > 1) dist = 1;

    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.1515;

    if (unit === 'K') dist = dist * 1.609344;
    if (unit === 'N') dist = dist * 0.8684;

    return dist;
  }
};

export const browser = () => {
  if (Platform.OS !== 'web') return '';
  const test = (regexp: RegExp) => regexp.test(window.navigator.userAgent);

  switch (true) {
    case test(/edg/i):
      return 'microsoft edge';
    case test(/trident/i):
      return 'internet explorer';
    case test(/firefox|fxios/i):
      return 'firefox';
    case test(/opr\//i):
      return 'opera';
    case test(/ucbrowser/i):
      return 'uc browser';
    case test(/samsungbrowser/i):
      return 'samsung browser';
    case test(/chrome|chromium|crios/i):
      return 'chrome';
    case test(/safari/i):
      return 'safari';
    default:
      return 'browser';
  }
};

// string to readable capitalization
export const toCapitalize = (str: string) => {
  if (typeof str !== 'string') return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const setContestFrameImage = (betType: string) => {
  if (betType === 'paytable') return image9;
  return image8;
};

let lastLocationUpdate: GeolocationPosition;

const getCurrentLocation = async (): Promise<GeolocationPosition> => {
  console.log('[GeoSmart] Starting geolocation process...');

  const tryGetPosition = (enableHighAccuracy: boolean, timeout: number, maximumAge: number): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve(position);
        },
        () => {
          reject(null);
        },
        {enableHighAccuracy, timeout, maximumAge}
      );
    });
  };

  try {
    return await tryGetPosition(true, 10000, 0);

  } catch (error) {
    console.warn('[GeoSmart] First attempt failed:', error);

    if (error instanceof GeolocationPositionError) {
      if (error.code === error.POSITION_UNAVAILABLE || error.code === error.TIMEOUT) {
        try {
          return await tryGetPosition(false, 20000, 0);

        } catch (error2) {
          console.warn('[GeoSmart] Second attempt failed:', error2);

          if (error2 instanceof GeolocationPositionError) {
            if (error2.code === error2.POSITION_UNAVAILABLE || error2.code === error2.TIMEOUT) {
              try {
                return await tryGetPosition(false, 10000, Infinity);

              } catch (error3) {
                throw error3;
              }

            } else {
              throw error2;
            }

          } else {
            throw error2;
          }
        }

      } else {
        throw error;
      }

    } else {
      throw error;
    }
  }
};

export const getLocation = async (): Promise<GeolocationPosition | null> => {
  try {
    if (!lastLocationUpdate || moment().diff(moment(lastLocationUpdate.timestamp), 'minutes')) {
      const newLocation = await getCurrentLocation();

      if (!lastLocationUpdate) {
        lastLocationUpdate = newLocation;

      } else {
        const currDistance = distance(lastLocationUpdate.coords.latitude, lastLocationUpdate.coords.longitude, newLocation.coords.latitude, newLocation.coords.longitude, 'K');
        console.log('current distance', currDistance);

        if (currDistance < 3.5) lastLocationUpdate = newLocation;
      }
    }

    return lastLocationUpdate;

  } catch (e) {
    console.log('getLocation e', e);
    return null;
  }
};

export const setVenueIdOrLocation = async (request: any) => {
  const curLocation = await getLocation();

  if (curLocation && curLocation.coords && curLocation.coords.latitude && curLocation.coords.longitude) {
    request.location = create(LocationSchema, {
      latitude: curLocation.coords.latitude, longitude: curLocation.coords.longitude
    });

  } else {
    throw {message: 'Permission to access the location was denied.'};
  }
};

export function convertCentsToDollars(cents: number): string {
  const dollars = cents / 100;
  return dollars.toFixed(2);
}

export const getCardTypeForApplePay = (type: 'Visa' | 'MasterCard') => {
  switch (type) {
    case 'Visa':
      return 'VI';
    case 'MasterCard':
      return 'MC';
    default:
      return null;
  }
};
